'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import PerformanceMetrics from './PerformanceMetrics';
import RevenueAnalytics from './RevenueAnalytics';
import DeliveryRates from './DeliveryRates';
import ExportPanel from './ExportPanel';

type TimeRange = '7' | '30' | '90' | '365';

export default function ReportsDashboard() {
  const [timeRange, setTimeRange] = useState<TimeRange>('30');
  const [activeTab, setActiveTab] = useState<'overview' | 'performance' | 'revenue' | 'delivery'>('overview');

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'ChartBarIcon' },
    { id: 'performance', label: 'Performance', icon: 'public/assets/images/ChatGPT_Image_Feb_7_2026_02_48_47_AM-1770428942096.png' },
    { id: 'revenue', label: 'Revenue', icon: 'CurrencyDollarIcon' },
    { id: 'delivery', label: 'Delivery Rates', icon: 'CheckCircleIcon' },
  ] as const;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Controls */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-8">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-foreground">Time Range:</label>
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value as TimeRange)}
            className="px-4 py-2 bg-card border border-border rounded-lg text-sm font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="7">Last 7 Days</option>
            <option value="30">Last 30 Days</option>
            <option value="90">Last 90 Days</option>
            <option value="365">Last Year</option>
          </select>
        </div>

        <ExportPanel timeRange={timeRange} />
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-8 border-b border-border pb-4">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-primary text-primary-foreground'
                : 'bg-card text-foreground hover:bg-muted border border-border'
            }`}
          >
            <Icon name={tab.icon} size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {activeTab === 'overview' && (
        <div className="space-y-8">
          <PerformanceMetrics timeRange={timeRange} />
          <RevenueAnalytics timeRange={timeRange} />
          <DeliveryRates timeRange={timeRange} />
        </div>
      )}
      {activeTab === 'performance' && <PerformanceMetrics timeRange={timeRange} detailed />}
      {activeTab === 'revenue' && <RevenueAnalytics timeRange={timeRange} detailed />}
      {activeTab === 'delivery' && <DeliveryRates timeRange={timeRange} detailed />}
    </div>
  );
}